import {
  ComplianceCaseResolution,
  ComplianceCaseStatus,
  ComplianceCaseTypeEnum,
} from "~/graphql/supergraphEnums";
import { type GetComplianceCaseQuery } from "~/graphql/supergraphTypes";
import { isInternalEscalationSource } from "~/pages/Compliance/utils/constants";

type ProfilesForCase = GetComplianceCaseQuery["complianceCase"] | null | undefined;

const SUPPORTED_CASE_TYPES = [
  ComplianceCaseTypeEnum.InvestorOnboarding,
  ComplianceCaseTypeEnum.PaymentInstructionChangeReview,
  ComplianceCaseTypeEnum.Remediation,
];

export const isAllowedCaseType = (
  caseType: string | ComplianceCaseTypeEnum | null | undefined,
  includeEntityClientOnboarding = false,
): boolean =>
  !!caseType &&
  (SUPPORTED_CASE_TYPES.includes(caseType as ComplianceCaseTypeEnum) ||
    (includeEntityClientOnboarding && isInternalEscalationSource(caseType)));

const REDIRECT_RESOLUTIONS = [
  ComplianceCaseResolution.IoUnderReviewEscalated,
  ComplianceCaseResolution.Escalated,
  ComplianceCaseResolution.IoUnderReviewAccepted,
  ComplianceCaseResolution.IoUnderReviewRejected,
  ComplianceCaseResolution.IoClosedAccepted,
  ComplianceCaseResolution.IoClosedRejected,
  ComplianceCaseResolution.ProposedAcceptance,
  ComplianceCaseResolution.ProposedRejection,
  ComplianceCaseResolution.Accepted,
  ComplianceCaseResolution.Rejected,
  ComplianceCaseResolution.IoClosed,
];

/** Verdicts an entity/client onboarding case reaches once its internal escalation resolves. */
const ONBOARDING_VERDICT_RESOLUTIONS = [
  ComplianceCaseResolution.ProposedApproved,
  ComplianceCaseResolution.ProposedDenied,
  ComplianceCaseResolution.Approved,
  ComplianceCaseResolution.Denied,
];

export const isRedirectResolution = (resolution: string | null | undefined): boolean =>
  !!resolution && REDIRECT_RESOLUTIONS.includes(resolution as ComplianceCaseResolution);

const SUCCESS_RESOLUTIONS = [
  ComplianceCaseResolution.IoUnderReviewAccepted,
  ComplianceCaseResolution.IoUnderReviewRejected,
  ComplianceCaseResolution.IoClosedAccepted,
  ComplianceCaseResolution.IoClosedRejected,
  ComplianceCaseResolution.ProposedAcceptance,
  ComplianceCaseResolution.ProposedRejection,
  ComplianceCaseResolution.Accepted,
  ComplianceCaseResolution.Rejected,
  ComplianceCaseResolution.IoClosed,
];

export const isSuccessResolution = (resolution: string | null | undefined): boolean =>
  !!resolution && SUCCESS_RESOLUTIONS.includes(resolution as ComplianceCaseResolution);

export const buildEscalationsLink = (
  caseId: number,
  profilesForCase: ProfilesForCase,
): string => {
  const clientId = profilesForCase?.externalArena?.objectId ?? "";
  return `${window.location.origin}/canopy/compliance/escalations?sourceCaseId=${caseId}&clientId=${clientId}`;
};

export const shouldRenderEscalationsMenu = (
  caseType: string | ComplianceCaseTypeEnum | null | undefined,
  escalations:
    NonNullable<GetComplianceCaseQuery["complianceCase"]>["escalations"] | null | undefined,
  includeEntityClientOnboarding = false,
): boolean => {
  return (
    isAllowedCaseType(caseType, includeEntityClientOnboarding) &&
    Boolean((escalations?.length ?? 0) > 0)
  );
};

export const getEscalationsMenuFlags = (
  caseResolution: string | null | undefined,
  caseId: number,
  profilesForCase: ProfilesForCase,
) => {
  const hasOnboardingVerdict =
    isInternalEscalationSource(profilesForCase?.caseType) &&
    ONBOARDING_VERDICT_RESOLUTIONS.includes(caseResolution as ComplianceCaseResolution);
  const hasRedirectResolution = hasOnboardingVerdict || isRedirectResolution(caseResolution);
  const hasSuccessResolution = hasOnboardingVerdict || isSuccessResolution(caseResolution);

  const childCases = profilesForCase?.childCases as
    | Array<{
        caseType: ComplianceCaseTypeEnum;
        status: ComplianceCaseStatus;
        resolution: ComplianceCaseResolution | null;
      }>
    | undefined;

  let allEscalationsClosed = false;
  let hasEscalationChildCases = false;

  if (childCases && childCases.length > 0) {
    const escalationChildCases = childCases.filter(
      (childCase) => childCase.caseType === ComplianceCaseTypeEnum.Escalation,
    );

    if (escalationChildCases.length > 0) {
      hasEscalationChildCases = true;
      allEscalationsClosed = escalationChildCases.every(
        (childCase) => childCase.status === ComplianceCaseStatus.Closed,
      );
    }
  }

  const linkMode = hasRedirectResolution && hasEscalationChildCases;
  const successMode = hasSuccessResolution && allEscalationsClosed;

  const linkUrl = buildEscalationsLink(caseId, profilesForCase);
  return { linkMode, successMode, linkUrl } as const;
};
